import { Link } from "react-router-dom";
import { Quote, Leaf, GraduationCap, Users, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

const META = [
  { id: "nutrition", icon: Leaf, image: IMAGES.produce },
  { id: "education", icon: GraduationCap, image: IMAGES.produceBasket },
  { id: "resources", icon: Users, image: IMAGES.community },
];

export default function Programs() {
  const { t } = useLang();
  const p = t.programs;

  return (
    <>
      <section className="bg-[#0A2540] text-white" data-testid="programs-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 sm:py-28">
          <Reveal>
            <Overline className="text-[#4ade80]">{p.heroOverline}</Overline>
            <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.05]">{p.heroTitle}</h1>
            <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">{p.heroSub}</p>
          </Reveal>
        </div>
      </section>

      {/* Program tabs */}
      <section className="py-20 sm:py-28" data-testid="initiatives-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Tabs defaultValue="nutrition" className="w-full">
            <TabsList className="flex flex-wrap h-auto justify-start gap-2 bg-transparent p-0 mb-10">
              {META.map((m, i) => {
                const Icon = m.icon;
                return (
                  <TabsTrigger
                    key={m.id}
                    value={m.id}
                    data-testid={`program-tab-${m.id}`}
                    className="rounded-full border border-[#E2E8F0] px-5 py-2.5 font-semibold text-[#475569] data-[state=active]:bg-[#16A34A] data-[state=active]:text-white data-[state=active]:border-[#16A34A]"
                  >
                    <Icon className="w-4 h-4 mr-2" aria-hidden="true" /> {p.items[i].name}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {META.map((m, i) => {
              const item = p.items[i];
              return (
                <TabsContent key={m.id} value={m.id} className="mt-0">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="order-2 lg:order-1">
                      <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2540] tracking-tight">{item.headline}</h2>
                      <p className="mt-5 text-lg text-[#475569] leading-relaxed">{item.body}</p>
                      <ul className="mt-6 space-y-3">
                        {item.points.map((pt, j) => (
                          <li key={j} className="flex items-start gap-3 text-[#0A2540] font-medium">
                            <span className="mt-1.5 w-2 h-2 rounded-full bg-[#E85D04] shrink-0" aria-hidden="true" />
                            {pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="order-1 lg:order-2">
                      <img src={m.image} alt={item.name} className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]" />
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* Success stories */}
      <section className="py-20 sm:py-28 bg-[#F1F5F9]" data-testid="stories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>{p.storiesOverline}</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl text-[#0A2540] tracking-tight">{p.storiesTitle}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {p.stories.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <Card className="h-full bg-white border-[#E2E8F0] rounded-2xl p-8 shadow-sm hover:shadow-md transition-all flex flex-col">
                  <Quote className="w-9 h-9 text-[#16A34A]/30" aria-hidden="true" />
                  <blockquote className="mt-4 text-[#0A2540] text-lg leading-relaxed flex-1">"{s.quote}"</blockquote>
                  <footer className="mt-6">
                    <div className="font-display font-semibold text-[#0A2540]">{s.author}</div>
                    <div className="text-sm text-[#475569]">{s.detail}</div>
                  </footer>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0A2540]">{p.ctaTitle}</h2>
          <Button asChild className="mt-6 bg-[#E85D04] hover:bg-[#CC5202] text-white font-semibold rounded-full h-12 px-8">
            <Link to="/donate" data-testid="programs-donate-button">
              {p.ctaButton} <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </>
  );
}
